/**
 * Shared popup content component for event markers
 * Used by both EventMarkerPopup (PassageMap) and AncientWorldMap
 */

import { Badge } from '@/components/ui/badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { DatePopover } from '@/components/ui/date-popover';
import { CategoryBadge } from '@/components/ui/category-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlacePopupContent } from './PlacePopupContent';
import { AIDisclaimerBadge } from '@/components/ui/ai-disclaimer-badge';

interface EventPopupContentProps {
    title: string;
    summary: string;
    description?: string;
    relevance?: string;
    date: DateProposal;
    alternativeDates?: DateProposal[];
    categories?: Array<{ category: string; subcategory?: string }>;
    confidence?: Importance;
    sources?: Source[];
    verseRef?: string;
    bookName?: string;
    placeName?: string;
    ruinsImageUrl?: string;
    reconstructionImageUrl?: string;
    placeEntity?: GeographicEntityWithRelevance;
    placeData?: Place;
    passageYear?: number | null;
    eventImageUrl?: string; // Historical event image
    generatedBy?: string;
}

export function EventPopupContent({
    title,
    summary,
    description,
    relevance,
    date,
    alternativeDates,
    categories,
    confidence,
    sources,
    verseRef,
    bookName,
    placeName,
    ruinsImageUrl,
    reconstructionImageUrl,
    placeEntity,
    placeData,
    passageYear,
    eventImageUrl,
    generatedBy
}: EventPopupContentProps) {
    const isDarkMode = useDarkMode();
    const [showPlaceDetails, setShowPlaceDetails] = useState(false);

    // Determine which place image to show (prefer reconstruction, fallback to ruins)
    const placeImageUrl = reconstructionImageUrl || ruinsImageUrl;

    return (
        <div className={`space-y-2 w-full sm:w-[320px] min-h-[120px] sm:max-h-[70vh] sm:overflow-y-auto ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col">
                    <h3 className="font-semibold text-base line-clamp-3">{title}</h3>
                    {verseRef && (
                        <Badge variant="outline" className={`text-[10px] w-fit mt-1 ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
                            {bookName ? `${bookName} ${verseRef}` : `Verse ${verseRef}`}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Event Image */}
            {eventImageUrl && (
                <div className="w-full">
                    <img
                        src={eventImageUrl}
                        alt={title}
                        className={`w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                        onClick={() => window.open(eventImageUrl, '_blank')}
                    />
                </div>
            )}

            {/* Place Image (if no event image) */}
            {!eventImageUrl && placeImageUrl && placeName && (
                <div className="w-full">
                    <img
                        src={placeImageUrl}
                        alt={placeName}
                        className={`w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-700' : 'border border-gray-200'}`}
                        onClick={() => window.open(placeImageUrl, '_blank')}
                    />
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {placeName}
                    </p>
                </div>
            )}

            <DatePopover
                date={date}
                alternativeDates={alternativeDates}
                title="Event Date"
            />

            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{summary}</div>

            {/* Historical description */}
            {description && (
                <div className={`space-y-1 p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Historical Context</p>
                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {description}
                    </p>
                </div>
            )}

            {/* Passage-specific relevance */}
            {relevance && (
                <div className={`space-y-1 p-2 rounded ${isDarkMode ? 'bg-blue-950/30' : 'bg-blue-50'}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                        {verseRef ? 'Passage Relevance' : 'Biblical Relevance'}
                    </p>
                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>
                        {relevance}
                    </p>
                </div>
            )}

            {confidence && (
                <ConfidenceBadge
                    confidence={confidence}
                    context="event"
                />
            )}

            {categories && categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {categories.map((cat, i) => (
                        <CategoryBadge key={i} category={cat.category} />
                    ))}
                </div>
            )}

            {/* Sources */}
            {sources && sources.length > 0 && (
                <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <SourceLinks sources={sources} />
                </div>
            )}

            {/* Place Details Section (expandable) */}
            {placeEntity && placeData && (
                <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPlaceDetails(!showPlaceDetails)}
                        className={`w-full flex items-center justify-between p-2 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                    >
                        <span className="text-sm font-medium">
                            {showPlaceDetails ? 'Hide' : 'Show'} Place Details
                        </span>
                        {showPlaceDetails ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </Button>

                    {showPlaceDetails && (
                        <div className="mt-2">
                            <PlacePopupContent
                                placeName={placeEntity.placeName}
                                type={placeEntity.type}
                                importance={placeEntity.importance}
                                summary={placeEntity.summary}
                                description={placeData.description}
                                biblicalRelevance={placeData.biblicalRelevance}
                                passageRelevance={placeEntity.relevanceReason}
                                modernReference={placeData.coordinates?.modernReference}
                                coordinates={{
                                    precision: placeData.coordinates?.precision,
                                    confidence: placeData.coordinates?.confidence
                                }}
                                ruinsImageUrl={placeData.images?.ruinsImageUrl}
                                reconstructionsByPeriod={placeData.images?.reconstructionsByPeriod}
                                passageYear={passageYear}
                                geography={placeData.geography}
                                significance={placeData.significance}
                                archaeology={placeData.archaeology}
                                historicalPeriods={placeData.historicalPeriods}
                                biblicalReferences={placeData.biblicalReferences}
                                relatedHistoricalEvents={placeData.relatedHistoricalEvents}
                                historicalMaps={placeData.historicalMaps}
                                sources={placeData.sources}
                                relevanceType={placeEntity.relevanceType}
                                relevanceReason={placeEntity.relevanceReason}
                                hideAIDisclaimer={true}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* AI Disclaimer at the very end */}
            <div className="pt-4 mt-4 flex justify-center border-t border-border/10">
                <AIDisclaimerBadge
                    model={generatedBy}
                    contentId={title}
                    contextType="event"
                />
            </div>
        </div>
    );
}

